import React, { useCallback, useState } from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { SlDialogRoot } from 'src/components/BeaconV2Components/SlDialog';
import { EditTopicGroupModal } from './EditTopicGroupModal';
import { useMainEntity, useMainEntityId } from 'src/utils/Hooks/reduxSelectors';
import { TopicGroup, Topic } from '../types';
import {
  TopicRequestBuilder,
  getTopicOrTopicGroupType,
  isStandardTopicOrTopicGroup,
  isOwnedTopicOrTopicGroup,
  useBaseTopicRequestBuilder
} from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils';
import { topicSettingsServerProxy, GetTopicGroupMetricsResponseRecord } from './serverProxy';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ComparisonPeriod,
  useAppSelector,
  useComparisonPeriod,
  useHistory,
  useMainTimePeriodShortDisplayName,
  useMetricFormatter,
  useSnackbar
} from 'src/utils/Hooks';
import { SxProps } from '@mui/system';
import { Box } from '@mui/material';
import { SlFlippableCard } from 'src/components/BeaconV2Components/SlFlippableCard';
import {
  SlCardHeader,
  SlCardTitle,
  SlCardSplineChart,
  SlCardContainer,
  SlCardFooter,
  SlCardBack
} from 'src/components/BeaconV2Components/SlCard';
import ElementCard from 'src/components/BeaconV2Components/ElementCard/ElementCard';
import _capitalize from 'lodash/capitalize';
import { SlPaginator } from 'src/components/BeaconV2Components/SlPaginator';
import { Link } from 'react-router-dom';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { SplineDataPoint } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/types';
import { calculatePercentChange } from 'src/utils/app';
import { SlChangeAnnotation } from 'src/components/BeaconV2Components/SlChangeAnnotation/SlChangeAnnotation';
import pluralize from 'pluralize';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { TopicTrafficByKeywords } from '../Total/TrafficGridTopics';
import { GenericErrorMessage } from 'src/components/BeaconV2Components/common/CommonSnackbarMessages/GenericErrorMessage';
import { UserMetaData, userMetadataServerProxy } from 'src/serverProxy/userMetadataServerProxy';
import _isNil from 'lodash/isNil';
import TopicOwner from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/TopicOwner';
import { StandardTopicSettingsSidebar } from './Sidebars/StandardTopicSettingsSidebar';
import { CustomTopicGroupSettingsSidebar } from './Sidebars/CustomTopicGroupSettingsSidebar';
import { DeleteTopicGroupModal } from './modals/DeleteTopicGroupModal';
import { useComparisonWeeksForSpline } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/hooks/useTopics';
import { hexToRgba, useStacklineTheme } from '@stackline/ui';
import {
  StorageSource,
  useTopicBuilderActions
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Store/useTopicBuilderStore';
import MiniButton from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/MiniButton';
import { DeleteTopicModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/modals/DeleteTopicModal';
import GenericCardLoading from 'src/components/BeaconV2Components/GenericCardLoading/GenericCardLoading';
import { useTopicGroupButtonOptionsStore } from 'src/zustandStores/BeaconPro/Topics/TopicGroups/useTopicGroupButtonOptionsStore';
import { useRemoveTopicModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/hooks/useRemoveTopicModal';
import { RemoveTopicModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/modals/RemoveTopicModal';
import { TOPIC_QUERY_KEYS } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/topicQueryKeys';
import { useAddTopicGroupStoreActions } from 'src/zustandStores/BeaconPro/Topics/TopicGroups/useAddTopicGroupStore';
import { useTopicTaxonomyOptions } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/hooks/useTopicTaxonomyOptions';
import { useRefreshTopicEntity } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/hooks/useRefreshTopicEntity';
import { useTopicPolling } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/hooks/useTopicPolling';

interface TopicGroupCardInfo extends GetTopicGroupMetricsResponseRecord {
  splineData: SplineDataPoint[];
  comparisonSplineData: SplineDataPoint[];
  totalTrafficPercentChange: number;
  ownership: 'Custom' | 'Standard' | 'Owned';
  type: 'topic';
  id: string;
  owner: UserMetaData;
  topicGroups: TopicGroup[];
  topicMetadata: Topic;
}

export const TOPIC_GROUPS_PER_CARD = 9;

const useGetTopicsForTopicGroup = () => {
  const retailerId = useAppSelector((state) => state.retailer.id);
  const comparisonPeriod = useComparisonPeriod();
  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);

  const { comparisonStartWeek, comparisonEndWeek } = useComparisonWeeksForSpline();

  const baseTopicRequest = useBaseTopicRequestBuilder();
  const topicGroupMetricsRequest = new TopicRequestBuilder(baseTopicRequest.build())
    .setStartWeekId(startWeek)
    .setEndWeekId(endWeek)
    .setSortField('totalTraffic')
    .build();

  const topicGroupMetricsComparisonRequest = new TopicRequestBuilder(topicGroupMetricsRequest)
    .setStartWeekId(comparisonStartWeek)
    .setEndWeekId(comparisonEndWeek)
    .build();

  const { data, isLoading } = useQuery({
    queryKey: [
      TOPIC_QUERY_KEYS.all,
      'GetTopicGroupMetrics',
      topicGroupMetricsRequest,
      topicGroupMetricsComparisonRequest
    ],
    queryFn: async () => {
      // Get Topic Group Metrics
      const [response, comparisonResponse] = await Promise.all([
        topicSettingsServerProxy.getTopicGroupMetrics(topicGroupMetricsRequest),
        topicSettingsServerProxy.getTopicGroupMetrics(topicGroupMetricsComparisonRequest)
      ]);

      // Create collection of unique topicIds
      const topicIds = [...new Set(response.records.map((record) => record.topicId))];

      const comparisonTopicIdMap = comparisonResponse.records.reduce((acc, record) => {
        acc[record.topicId] = record;
        return acc;
      }, {} as Record<string, GetTopicGroupMetricsResponseRecord>);

      const [topicGroupsByWeek, comparisonTopicGroupsByWeek, topicIdToMetadata, topicIdToTopicGroupsMap] =
        await Promise.all([
          // Get Topic Metrics by Week
          topicSettingsServerProxy.getTopicMetricsByWeek(
            new TopicRequestBuilder(baseTopicRequest.build())
              .setStartWeekId(startWeek)
              .setEndWeekId(endWeek)
              .setTopicIds(topicIds)
              .setSortField('weekId')
              .build()
          ),

          topicSettingsServerProxy.getTopicMetricsByWeek(
            new TopicRequestBuilder(baseTopicRequest.build())
              .setStartWeekId(comparisonStartWeek)
              .setEndWeekId(comparisonEndWeek)
              .setTopicIds(topicIds)
              .setSortField('weekId')
              .build()
          ),
          // Get Topic Metadata
          topicSettingsServerProxy.bulkGetTopics(topicIds),

          // Get list of topic groups that contain these topics
          topicSettingsServerProxy
            .getTopicGroupsContainingTopics({
              retailerId: +retailerId,
              topicIds
            })
            .then(async (topicsWithGroups) => {
              const topicGroupIds = topicsWithGroups.records.reduce((acc, record) => {
                record.topicGroupIds.slice(0, TOPIC_GROUPS_PER_CARD).forEach((topicGroupId) => {
                  acc.add(topicGroupId);
                });
                return acc;
              }, new Set<string>());

              // Get topic groups
              const topicGroupsMap = await topicSettingsServerProxy.bulkGetTopicGroups([...topicGroupIds]);

              return topicsWithGroups.records.reduce((acc, record) => {
                acc[record.topicId] = record.topicGroupIds
                  .slice(0, TOPIC_GROUPS_PER_CARD)
                  .map((topicGroupId) => topicGroupsMap[topicGroupId]);
                return acc;
              }, {} as Record<string, TopicGroup[]>);
            })
        ]);

      async function getUserMetadata() {
        const userIds = [...new Set(Object.values(topicIdToMetadata).map((topic) => topic.ownedByUserId))].filter(
          (item) => !_isNil(item)
        );

        if (userIds.length < 1) {
          return {};
        }

        const userMetadata = await userMetadataServerProxy.getUserProfiles(userIds);
        return userMetadata;
      }
      // Get User Metadata
      const userMetadata = await getUserMetadata();

      const responsesWithSpline: TopicGroupCardInfo[] = response.records.map((record) => {
        // Generate weekId-based spline data (no timestamp conversion needed)
        const splineData: SplineDataPoint[] = topicGroupsByWeek.records
          .filter((r) => r.topicId === record.topicId)
          .sort((a, b) => a.weekId - b.weekId)
          .map((r) => ({ weekId: r.weekId, value: r.totalTraffic }));

        // Comparison data uses original weekIds - NO year incrementing needed
        // SplineChartV2 handles the alignment via categories
        const comparisonSplineData: SplineDataPoint[] = comparisonTopicGroupsByWeek.records
          .filter((weekRecord) => weekRecord.topicId === record.topicId)
          .map((weekRecord) => ({ weekId: weekRecord.weekId, value: weekRecord.totalTraffic }))
          .sort((a, b) => a.weekId - b.weekId);

        // For prior period, prepend the last comparison point to primary data for continuity
        const finalSplineData: SplineDataPoint[] = comparisonPeriod === ComparisonPeriod.PRIOR_PERIOD && comparisonSplineData.length > 0
          ? [comparisonSplineData[comparisonSplineData.length - 1], ...splineData]
          : splineData;

        const topicMetadata = topicIdToMetadata[record.topicId];
        return {
          ...record,
          id: record.topicId,
          type: 'topic',
          splineData: finalSplineData,
          comparisonSplineData,
          totalTrafficPercentChange: calculatePercentChange(
            record.totalTraffic,
            comparisonTopicIdMap[record.topicId]?.totalTraffic ?? 0
          ),
          ownership: getTopicOrTopicGroupType(topicMetadata?.sharedWithEntityId, topicMetadata?.ownedByUserId),
          owner: userMetadata[topicMetadata.ownedByUserId],
          topicGroups: topicIdToTopicGroupsMap[record.topicId],
          topicMetadata
        };
      });

      return responsesWithSpline;
    }
  });
  return { topics: data ?? [], isLoading };
};

const MiniTopicCard = ({ topic }: { topic: TopicGroupCardInfo }) => {
  const formatMetric = useMetricFormatter();
  const { searchParams, additionalParams } = useAppSelector((state) => state.app.queryParams);
  const theme = useStacklineTheme();
  const history = useHistory();
  const topicGroupEntity = useMainEntity();
  const topicGroupOwnership = getTopicOrTopicGroupType(
    topicGroupEntity?.sharedWithEntityId,
    topicGroupEntity?.ownedByUserId
  );
  const { openModal: openRemoveTopicModal } = useRemoveTopicModal();

  const { storage } = useTopicBuilderActions();

  const miniButtonStyles = {
    backgroundColor:
      topic.type === 'topic' ? hexToRgba(theme.colors.accentTangerine, 0.1) : hexToRgba(theme.colors.accentPlum, 0.1)
  };
  const sx = {
    ...miniButtonStyles,
    '&:hover': {
      ...miniButtonStyles
    }
  };

  const buttonColor = topic.type === 'topic' ? 'accentTangerine' : 'accentPlum';

  const handleDuplicateClick = useCallback(() => {
    const { topicMetadata } = topic;
    // Save the topic to local storage so that it can be retrieved when the user navigates to the topic builder
    storage.save(topicMetadata, StorageSource.DUPLICATE);

    history.push(`/search${searchParams}&entityType=searchtermlist&action=create`);
  }, [history, searchParams, topic, storage]);

  const handleEditClick = useCallback(() => {
    // No need to save the topic to local storage since the TopicSettingsPage will pull the topic from the entity
    history.push(`/topic/${topic.id}${searchParams}&tab=traffic&subtab=settings`);
  }, [history, searchParams, topic]);

  const handleRemove = useCallback(() => {
    openRemoveTopicModal({ topicId: topic.id, topicName: topic.topicName, topicGroupId: `${topicGroupEntity.id}` });
  }, [openRemoveTopicModal, topic.id, topic.topicName, topicGroupEntity.id]);

  const renderButtons = useCallback(() => {
    switch (topicGroupOwnership) {
      case 'Owned':
        switch (topic.ownership) {
          case 'Owned':
            return (
              <Flex gap={8}>
                <MiniButton onClick={handleEditClick} sx={sx}>
                  <Text color={buttonColor}>Edit</Text>
                </MiniButton>
                <MiniButton onClick={handleRemove} sx={sx}>
                  <Text color={buttonColor}>Remove</Text>
                </MiniButton>
                <MiniButton onClick={handleDuplicateClick} sx={sx}>
                  <Text color={buttonColor}>Duplicate</Text>
                </MiniButton>
              </Flex>
            );

          case 'Custom':
            return (
              <Flex gap={8}>
                <MiniButton onClick={handleRemove} sx={sx}>
                  <Text color={buttonColor}>Remove</Text>
                </MiniButton>
                <MiniButton onClick={handleDuplicateClick} sx={sx}>
                  <Text color={buttonColor}>Duplicate</Text>
                </MiniButton>
              </Flex>
            );

          default:
            return null;
        }

      case 'Custom':
        switch (topic.ownership) {
          case 'Owned':
            return (
              <Flex gap={8}>
                <MiniButton onClick={handleEditClick} sx={sx}>
                  <Text color={buttonColor}>Edit</Text>
                </MiniButton>

                <MiniButton onClick={handleDuplicateClick} sx={sx}>
                  <Text color={buttonColor}>Duplicate</Text>
                </MiniButton>
              </Flex>
            );

          case 'Custom':
            return (
              <Flex gap={8}>
                <MiniButton onClick={handleDuplicateClick} sx={sx}>
                  <Text color={buttonColor}>Duplicate</Text>
                </MiniButton>
              </Flex>
            );

          default:
            return null;
        }

      case 'Standard':
        return null;

      default:
        return null;
    }
  }, [buttonColor, sx, handleRemove, topicGroupOwnership, topic, handleDuplicateClick, handleEditClick]);

  return (
    <SlFlippableCard
      sx={{
        height: '416px',
        width: '250px'
      }}
      flipIconSize="16px"
      key={topic.topicId}
      cardFront={() => (
        <SlCardContainer>
          <SlCardHeader>
            <ElementCard textSx={{ fontWeight: 700 }} variant="extraSmall" color="accentTangerine">
              {_capitalize(topic.topicName.slice(0, 2))}
            </ElementCard>
            <SlCardTitle>
              <Link to={`/topic/${topic.topicId}${searchParams}${additionalParams}`}>{topic.topicName}</Link>
            </SlCardTitle>
          </SlCardHeader>
          <SlCardSplineChart
            metricType={METRICTYPE.VOLUME}
            primaryData={topic.splineData}
            secondaryData={topic.comparisonSplineData}
            height={64}
            width={244}
          />
          <SlCardFooter>
            <Flex flexDirection="column" gap={4}>
              <Text
                sx={{
                  fontSize: '12px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 'bold'
                }}
                transform="uppercase"
              >
                Total Traffic
              </Text>
              <Flex gap="sm" alignItems="center">
                <Text variant="body2">
                  {formatMetric(topic.totalTraffic, METRICTYPE.VOLUME, {
                    decimalPlaces: 1,
                    showFullValue: false
                  })}
                </Text>
                <SlChangeAnnotation
                  value={topic.totalTrafficPercentChange}
                  formattedValue={`${formatMetric(topic.totalTrafficPercentChange, METRICTYPE.PERCENT, {
                    showFullValue: false
                  })} YTD`}
                />
              </Flex>
            </Flex>
          </SlCardFooter>
        </SlCardContainer>
      )}
      cardBack={() => (
        <SlCardBack>
          <Flex flexDirection="column" gap="md">
            {renderButtons()}
            <Flex flexDirection="column" gap="sm">
              <Text variant="subtitle3" transform="uppercase">
                Topic Type
              </Text>
              <Text variant="body3">{topic.ownership}</Text>
            </Flex>
            <Flex flexDirection="column" gap="sm">
              <Text variant="subtitle3" transform="uppercase">
                Topic Group Inclusions
              </Text>
              {topic.topicGroups.map((topicGroup) => (
                <Text
                  sx={{
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    width: '180px',
                    display: 'block',
                    overflow: 'hidden'
                  }}
                  key={topicGroup.topicGroupId}
                  variant="body3"
                >
                  {topicGroup.topicGroupName}
                </Text>
              ))}
              {topic.topicGroups.length >= TOPIC_GROUPS_PER_CARD && (
                <Link to={`/topic/${topic.topicId}${searchParams}${additionalParams}&anchor=topic-group-inclusions`}>
                  <Text
                    underlined
                    sx={{
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}
                  >
                    Show more
                  </Text>
                </Link>
              )}
            </Flex>
          </Flex>
          <SlCardFooter sx={{ padding: 0 }}>
            <TopicOwner
              createdAt={topic?.topicMetadata?.createdAt}
              firstName={topic?.owner?.firstName}
              lastName={topic?.owner?.lastName}
              userId={topic?.topicMetadata?.ownedByUserId}
            />
          </SlCardFooter>
        </SlCardBack>
      )}
    />
  );
};

function TopicGroupTopicCards({ sx }: { sx?: SxProps }) {
  const mainPeriodName = useMainTimePeriodShortDisplayName();

  const { topics, isLoading } = useGetTopicsForTopicGroup();

  return (
    <Box sx={sx}>
      {!isLoading && (
        <Text sx={{ marginBottom: '32px' }} variant="body1">{`This topic group displays the total traffic for ${
          topics?.length
        } ${pluralize('topic', topics?.length)} during the ${mainPeriodName} period.`}</Text>
      )}
      <SlPaginator
        loading={isLoading}
        renderLoader={() => (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
            {Array.from({ length: 20 }).map((_, index) => (
              <GenericCardLoading width={250} height={416} key={index} />
            ))}
          </Box>
        )}
        items={topics ?? []}
        itemsPerPage={20}
        renderItems={(items) => (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '14px'
            }}
          >
            {items.map((record: TopicGroupCardInfo) => (
              <MiniTopicCard key={record.id} topic={record} />
            ))}
          </Box>
        )}
      />
    </Box>
  );
}

/**
 * Mutation to save the included topics to a topic group
 */
const useSaveTopicGroupSettingsMutation = () => {
  const mainEntity = useMainEntity() as unknown as TopicGroup;
  const entityId = useMainEntityId();
  const currentTopicIds = mainEntity?.topicIds ?? [];
  const taxonomyOptions = useTopicTaxonomyOptions();
  const refreshEntity = useRefreshTopicEntity(mainEntity);
  const { pollTopicGroupUpdate } = useTopicPolling();

  return useMutation({
    mutationFn: async ({ selectedTopics }: { selectedTopics: string[] }) => {
      const topicIdsToRemove = currentTopicIds.filter((topicId) => !selectedTopics.includes(topicId));
      const topicIdsToAdd = selectedTopics.filter((topicId) => !currentTopicIds.includes(topicId));

      if (topicIdsToAdd.length) {
        await topicSettingsServerProxy.addTopicsToTopicGroup({
          topicIds: topicIdsToAdd,
          topicGroupId: entityId.toString(),
          taxonomyOptions
        });
      }

      if (topicIdsToRemove.length) {
        await topicSettingsServerProxy.removeTopicsFromTopicGroup({
          topicIds: topicIdsToRemove,
          topicGroupId: entityId.toString(),
          taxonomyOptions
        });
      }
    },
    onSuccess: () => {
      pollTopicGroupUpdate(`${entityId}`, `removing-topic-topicGroup-${entityId}`, refreshEntity);
    }
  });
};

export function TopicGroupSettingsPage() {
  const mainEntity = useMainEntity() as unknown as TopicGroup | Topic;
  const [view, setView] = useState('topics');
  const mainPeriodName = useMainTimePeriodShortDisplayName();
  const { showSnackbar } = useSnackbar();
  const [modalOpen, setModalOpen] = useState(false);
  const { setMode } = useTopicGroupButtonOptionsStore();
  const { setTopicGroupState } = useAddTopicGroupStoreActions();

  const handleViewChange = (option: 'topics' | 'keywords') => {
    setView(option);
    setMode(option);
  };

  const { isLoading: saveLoading, mutateAsync: saveTopicGroupSettings } = useSaveTopicGroupSettingsMutation();

  const handleSave = async (selectedTopicIds: string[], handleClear: () => void) => {
    if (saveLoading) {
      return;
    }

    try {
      await saveTopicGroupSettings({ selectedTopics: selectedTopicIds });
      showSnackbar({
        type: 'success',
        message: `You've successfully added ${selectedTopicIds.length} ${pluralize(
          'topic',
          selectedTopicIds.length
        )} to your topic group.`
      });
    } catch (e) {
      console.error('Error adding topics to topic group', e);
      showSnackbar({
        type: 'error',
        message: <GenericErrorMessage />
      });
    } finally {
      setModalOpen(false);
      handleClear();
    }
  };

  return (
    <>
      <BasePageContainer
        sx={{
          marginTop: '29px'
        }}
        sidebar={
          isStandardTopicOrTopicGroup(mainEntity.sharedWithEntityId) ? (
            <StandardTopicSettingsSidebar />
          ) : (
            <CustomTopicGroupSettingsSidebar />
          )
        }
        sidebarContainerSx={{
          marginTop: isStandardTopicOrTopicGroup(mainEntity.sharedWithEntityId) ? '46px' : '16px'
        }}
      >
        <Flex justifyContent="space-between">
          <SlDropdownMenu
            buttonVariant="title"
            options={[
              {
                id: 'topics',
                label: 'Topics'
              },
              {
                id: 'keywords',
                label: 'Keywords'
              }
            ]}
            selectedId={view}
            onChange={(option) => handleViewChange(option.id)}
          />
          {isOwnedTopicOrTopicGroup(mainEntity.ownedByUserId) && (
            <SlButton
              disabled={saveLoading}
              variant="contained"
              buttonSx={{
                paddingX: '12px'
              }}
              onClick={() => {
                setModalOpen(true);
                setTopicGroupState(({ setTopicIds }) => setTopicIds(mainEntity?.topicIds ?? []));
              }}
            >
              Edit Selected Topics
            </SlButton>
          )}
        </Flex>
        <Box sx={{ marginTop: '16px' }}>
          {view === 'topics' ? (
            <TopicGroupTopicCards />
          ) : (
            <TopicTrafficByKeywords
              trafficType="totalTraffic"
              buildSubtitle={({ response }) => {
                const uniqueKeywords = [...new Set(response[0].records.map((record) => record.searchTerm))];
                return `This topic group displays the total traffic for ${uniqueKeywords.length} unique keywords during the ${mainPeriodName} period.`;
              }}
            />
          )}
        </Box>
      </BasePageContainer>
      <SlDialogRoot>
        <EditTopicGroupModal
          title="Edit Selected Topics"
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          footer={({ handleClear, selectedTopicIds }) => (
            <Flex justifyContent="space-between">
              <SlButton onClick={() => handleClear()}>Clear</SlButton>
              <SlButton
                variant="contained"
                onClick={() => handleSave(selectedTopicIds, handleClear)}
                disabled={selectedTopicIds.length < 1}
              >
                {saveLoading ? 'Saving...' : 'Save'}
              </SlButton>
            </Flex>
          )}
        />
      </SlDialogRoot>
      <DeleteTopicGroupModal />
      <DeleteTopicModal />
      <RemoveTopicModal />
    </>
  );
}
